import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from './App';
import { landingLoader } from './api/loader';
import ErrorBoundary from './components/ErrorBoundary';
import ShopRedirect from './components/ShopRedirect';
import { withItDomainAliases } from './config/itRoutes';

const Landing = lazy(() => import('./pages/Landing'));
const StatisticsFalcon = lazy(() => import('./pages/StatisticsFalcon'));
const Login = lazy(() => import('./pages/Login'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));
const OrderStatus = lazy(() => import('./pages/OrderStatus'));
const Reinigung = lazy(() => import('./pages/Reinigung'));
const Versand = lazy(() => import('./pages/Versand'));
const Marktplaetze = lazy(() => import('./pages/Marktplaetze'));
const Plattform = lazy(() => import('./pages/Plattform'));
const WeretradeIT = lazy(() => import('./pages/WeretradeIT'));
const BauKlotzBude = lazy(() => import('./pages/BauKlotzBude'));
const GrowthStory = lazy(() => import('./pages/GrowthStory'));
const Entwicklung = lazy(() => import('./pages/Entwicklung'));
const ClaudeSnippetsManager = lazy(() => import('./pages/ClaudeSnippetsManager'));
const ShopBeaverAdminSandbox = lazy(() => import('./pages/ShopBeaverAdminSandbox'));
const OsintOwlExplorer = lazy(() => import('./pages/OsintOwlExplorer'));
const BkbSyncCenter = lazy(() => import('./pages/BkbSyncCenter'));
const LiteLlmDashboard = lazy(() => import('./pages/LiteLlmDashboard'));
const ScanopyMonitorDashboard = lazy(() => import('./pages/ScanopyMonitorDashboard'));
const SecurityFerretDashboard = lazy(() => import('./pages/SecurityFerretDashboard'));
const AffiliateFalconDashboard = lazy(() => import('./pages/AffiliateFalconDashboard'));
const ServiceAntDocsDashboard = lazy(() => import('./pages/ServiceAntDocsDashboard'));
const CommandCobraDashboard = lazy(() => import('./pages/CommandCobraDashboard'));
const InfrastructureDeepDive = lazy(() => import('./pages/InfrastructureDeepDive'));
const HumanVsLlm = lazy(() => import('./pages/HumanVsLlm'));
const OrchestrationVsFineTuning = lazy(() => import('./pages/OrchestrationVsFineTuning'));
const ClaudeMultiSession = lazy(() => import('./pages/ClaudeMultiSession'));
const KiAutomatisierung = lazy(() => import('./pages/KiAutomatisierung'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const ShippingReturns = lazy(() => import('./pages/ShippingReturns'));
const Faq = lazy(() => import('./pages/Faq'));
const AccountingFoxShowcase = lazy(() => import('./pages/AccountingFoxShowcase'));
const InventarOwlShowcase = lazy(() => import('./pages/InventarOwlShowcase'));
const MarketSharkShowcase = lazy(() => import('./pages/MarketSharkShowcase'));
const OsintOwlShowcase = lazy(() => import('./pages/OsintOwlShowcase'));
const InfrastructureDogShowcase = lazy(() => import('./pages/InfrastructureDogShowcase'));
const AnsichtAufZuruf = lazy(() => import('./pages/AnsichtAufZuruf'));
const CloudflareWorkersTipps = lazy(() => import('./pages/CloudflareWorkersTipps'));
const HonoD1Database = lazy(() => import('./pages/HonoD1Database'));
const DevOpsMonitoring = lazy(() => import('./pages/DevOpsMonitoring'));
const ServicesAi = lazy(() => import('./pages/ServicesAi'));
const PerformanceShowcase = lazy(() => import('./pages/PerformanceShowcase'));
const AutonomousVsGuided = lazy(() => import('./pages/AutonomousVsGuided'));
const ClaudeMemory = lazy(() => import('./pages/ClaudeMemory'));
const ClaudeCodeWorkflows = lazy(() => import('./pages/ClaudeCodeWorkflows'));
const SelfHealingCi = lazy(() => import('./pages/SelfHealingCi'));
const MultiModelCostRouting = lazy(() => import('./pages/MultiModelCostRouting'));
const RealtimeInventorySync = lazy(() => import('./pages/RealtimeInventorySync'));
const ResilientScrapingArchitecture = lazy(() => import('./pages/ResilientScrapingArchitecture'));
const SqliteEdgeVectorRag = lazy(() => import('./pages/SqliteEdgeVectorRag'));
const RealtimeOrderStreamPipeline = lazy(() => import('./pages/RealtimeOrderStreamPipeline'));
const MultiTenantRagIsolation = lazy(() => import('./pages/MultiTenantRagIsolation'));
const DurableObjectSqliteStateMachine = lazy(() => import('./pages/DurableObjectSqliteStateMachine'));
const EdgeSemanticCacheKv = lazy(() => import('./pages/EdgeSemanticCacheKv'));
const VectorizeHnswHybridSearch = lazy(() => import('./pages/VectorizeHnswHybridSearch'));
const CloudflareDurableStreams = lazy(() => import('./pages/CloudflareDurableStreams'));
const HyperdriveConnectionPooling = lazy(() => import('./pages/HyperdriveConnectionPooling'));
const ZeroTrustVectorRouting = lazy(() => import('./pages/ZeroTrustVectorRouting'));
const CloudflareWorkflowsEngine = lazy(() => import('./pages/CloudflareWorkflowsEngine'));
const CloudflareKvConsistencyModel = lazy(() => import('./pages/CloudflareKvConsistencyModel'));
const CloudflareD1DistributedSqlite = lazy(() => import('./pages/CloudflareD1DistributedSqlite'));
const CloudflareVectorizeMetadataIndexing = lazy(() => import('./pages/CloudflareVectorizeMetadataIndexing'));
const CloudflareDurableObjectsSqlite = lazy(() => import('./pages/CloudflareDurableObjectsSqlite'));
const CloudflareHyperdriveTcpMultiplexing = lazy(() => import('./pages/CloudflareHyperdriveTcpMultiplexing'));
const CloudflareZeroTrustAccessMesh = lazy(() => import('./pages/CloudflareZeroTrustAccessMesh'));
const CloudflareQueuesIdempotentProcessing = lazy(() => import('./pages/CloudflareQueuesIdempotentProcessing'));
const CloudflareBrowserRenderingFleet = lazy(() => import('./pages/CloudflareBrowserRenderingFleet'));
const CloudflareKvGlobalSessionReplication = lazy(() => import('./pages/CloudflareKvGlobalSessionReplication'));
const CloudflareVectorizeHybridSearchPipeline = lazy(() => import('./pages/CloudflareVectorizeHybridSearchPipeline'));
const CloudflareDurableObjectsWebStandardsSqlite = lazy(() => import('./pages/CloudflareDurableObjectsWebStandardsSqlite'));
const CloudflareWorkersAiFunctionCallingAgent = lazy(() => import('./pages/CloudflareWorkersAiFunctionCallingAgent'));
const CloudflareHyperdriveTcpConnectionPooling = lazy(() => import('./pages/CloudflareHyperdriveTcpConnectionPooling'));
const CloudflarePipelinesStreamIngest = lazy(() => import('./pages/CloudflarePipelinesStreamIngest'));
const CloudflareD1ReadReplicationOffload = lazy(() => import('./pages/CloudflareD1ReadReplicationOffload'));
const CloudflareRpcServiceBindingsMesh = lazy(() => import('./pages/CloudflareRpcServiceBindingsMesh'));
const CloudflareCallsWebRtcMediaMesh = lazy(() => import('./pages/CloudflareCallsWebRtcMediaMesh'));
const CloudflareWorkersAssetsRouting = lazy(() => import('./pages/CloudflareWorkersAssetsRouting'));
const CloudflareBrowserRenderingPlaywrightGrid = lazy(() => import('./pages/CloudflareBrowserRenderingPlaywrightGrid'));
const CloudflareKvConsistencyReplicationMesh = lazy(() => import('./pages/CloudflareKvConsistencyReplicationMesh'));
const CloudflareDurableObjectsSqliteStorageMesh = lazy(() => import('./pages/CloudflareDurableObjectsSqliteStorageMesh'));
const CloudflareVectorizeHybridRagAnnIndex = lazy(() => import('./pages/CloudflareVectorizeHybridRagAnnIndex'));
const CloudflareWorkflowsIdempotentExecutionEngine = lazy(() => import('./pages/CloudflareWorkflowsIdempotentExecutionEngine'));
const CloudflareHyperdriveConnectionPoolingMesh = lazy(() => import('./pages/CloudflareHyperdriveConnectionPoolingMesh'));
const CloudflareKvEventualConsistencyCachingMesh = lazy(() => import('./pages/CloudflareKvEventualConsistencyCachingMesh'));
const CloudflareQueuesFifoConcurrencyThrottle = lazy(() => import('./pages/CloudflareQueuesFifoConcurrencyThrottle'));
const CloudflareBrowserRenderingPdfGenerationMesh = lazy(() => import('./pages/CloudflareBrowserRenderingPdfGenerationMesh'));
const CloudflareAiGatewaySemanticCacheRouting = lazy(() => import('./pages/CloudflareAiGatewaySemanticCacheRouting'));
const CloudflareWorkersForPlatformsDynamicDispatch = lazy(() => import('./pages/CloudflareWorkersForPlatformsDynamicDispatch'));
const CloudflareVectorizeMetadataFilteringKnnPartitioning = lazy(() => import('./pages/CloudflareVectorizeMetadataFilteringKnnPartitioning'));
const CloudflareD1ReadReplicationSessions = lazy(() => import('./pages/CloudflareD1ReadReplicationSessions'));
const CloudflareTurnstileManagedChallengeBotProtection = lazy(() => import('./pages/CloudflareTurnstileManagedChallengeBotProtection'));
const CloudflareKvMetadataTtlPurge = lazy(() => import('./pages/CloudflareKvMetadataTtlPurge'));
const CloudflareQueuesDeadLetterConsumerBackoff = lazy(() => import('./pages/CloudflareQueuesDeadLetterConsumerBackoff'));
const CloudflareR2ObjectStorageEgressMesh = lazy(() => import('./pages/CloudflareR2ObjectStorageEgressMesh'));
const CloudflareImagesTransformationsMesh = lazy(() => import('./pages/CloudflareImagesTransformationsMesh'));



const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<div className="page-loading">Lädt…</div>}>
    {children}
  </Suspense>
);

const isWeretradeIt = typeof window !== 'undefined' && (
  window.location.hostname === 'weretradeit.de' ||
  window.location.hostname === 'www.weretradeit.de'
);

const WtRouteHandler: React.FC = () => {
  React.useEffect(() => {
    if (typeof window !== 'undefined' && !window.location.hostname.includes('weretradeit.de') && window.location.hostname !== 'localhost') {
      window.location.href = 'https://weretradeit.de/';
    }
  }, []);
  return <WeretradeIT />;
};

export const router = createBrowserRouter([
  {
    path: '/',
    id: 'root',
    element: <App />,
    loader: landingLoader,
    errorElement: <ErrorBoundary />,
    children: withItDomainAliases([
      {
        index: true,
        element: <SuspenseWrapper>{isWeretradeIt ? <WeretradeIT /> : <Landing />}</SuspenseWrapper>,
      },
      {
        // Der Shop lebt auf bauklotzbude.de (src/config/bkbShop.ts). `shop/*`
        // trifft auch /shop selbst.
        path: 'shop/*',
        element: <ShopRedirect />,
      },
      {
        path: 'order/:id',
        element: <SuspenseWrapper><OrderStatus /></SuspenseWrapper>,
      },
      {
        path: 'stats',
        element: <Navigate to="/bkb/stats" replace />,
      },
      {
        path: 'bkb/stats',
        element: <SuspenseWrapper><StatisticsFalcon /></SuspenseWrapper>,
      },
      {
        path: 'login',
        element: <SuspenseWrapper><Login /></SuspenseWrapper>,
      },
      {
        path: 'register',
        element: <SuspenseWrapper><Login /></SuspenseWrapper>,
      },
      {
        path: 'forgot',
        element: <SuspenseWrapper><Login /></SuspenseWrapper>,
      },
      {
        path: 'reset',
        element: <SuspenseWrapper><Login /></SuspenseWrapper>,
      },
      {
        path: 'reinigung',
        element: <SuspenseWrapper><Reinigung /></SuspenseWrapper>,
      },
      {
        path: 'bkb/versand',
        element: <SuspenseWrapper><Versand /></SuspenseWrapper>,
      },
      {
        path: 'bkb/marktplaetze',
        element: <SuspenseWrapper><Marktplaetze /></SuspenseWrapper>,
      },
      {
        path: 'wt',
        element: <SuspenseWrapper><WtRouteHandler /></SuspenseWrapper>,
      },
      {
        path: 'wt/plattform',
        element: <SuspenseWrapper><Plattform /></SuspenseWrapper>,
      },
      {
        path: 'wt/entwicklung',
        element: <SuspenseWrapper><Entwicklung /></SuspenseWrapper>,
      },
      {
        path: 'wt/growth',
        element: <SuspenseWrapper><GrowthStory /></SuspenseWrapper>,
      },
      {
        path: 'wt/claude-snippets-manager',
        element: <SuspenseWrapper><ClaudeSnippetsManager /></SuspenseWrapper>,
      },
      {
        path: 'wt/infrastructure',
        element: <SuspenseWrapper><InfrastructureDeepDive /></SuspenseWrapper>,
      },
      {
        path: 'wt/human-vs-llm',
        element: <SuspenseWrapper><HumanVsLlm /></SuspenseWrapper>,
      },
      {
        path: 'wt/autonomous-vs-guided',
        element: <SuspenseWrapper><AutonomousVsGuided /></SuspenseWrapper>,
      },
      {
        path: 'wt/orchestration',
        element: <SuspenseWrapper><OrchestrationVsFineTuning /></SuspenseWrapper>,
      },
      {
        path: 'wt/orchestration-vs-finetuning',
        element: <SuspenseWrapper><OrchestrationVsFineTuning /></SuspenseWrapper>,
      },
      {
        path: 'wt/claude-multi-session',
        element: <SuspenseWrapper><ClaudeMultiSession /></SuspenseWrapper>,
      },
      {
        path: 'wt/claude-memory',
        element: <SuspenseWrapper><ClaudeMemory /></SuspenseWrapper>,
      },
      {
        path: 'wt/claude-code-workflows',
        element: <SuspenseWrapper><ClaudeCodeWorkflows /></SuspenseWrapper>,
      },
      {
        path: 'claude-code-workflows',
        element: <SuspenseWrapper><ClaudeCodeWorkflows /></SuspenseWrapper>,
      },
      {
        path: 'wt/self-healing-ci',
        element: <SuspenseWrapper><SelfHealingCi /></SuspenseWrapper>,
      },
      {
        path: 'self-healing-ci',
        element: <SuspenseWrapper><SelfHealingCi /></SuspenseWrapper>,
      },
      {
        path: 'wt/multi-model-cost-routing',
        element: <SuspenseWrapper><MultiModelCostRouting /></SuspenseWrapper>,
      },
      {
        path: 'multi-model-cost-routing',
        element: <SuspenseWrapper><MultiModelCostRouting /></SuspenseWrapper>,
      },
      {
        path: 'wt/realtime-inventory-sync',
        element: <SuspenseWrapper><RealtimeInventorySync /></SuspenseWrapper>,
      },
      {
        path: 'realtime-inventory-sync',
        element: <SuspenseWrapper><RealtimeInventorySync /></SuspenseWrapper>,
      },
      {
        path: 'wt/resilient-scraping-architecture',
        element: <SuspenseWrapper><ResilientScrapingArchitecture /></SuspenseWrapper>,
      },
      {
        path: 'resilient-scraping-architecture',
        element: <SuspenseWrapper><ResilientScrapingArchitecture /></SuspenseWrapper>,
      },
      {
        path: 'wt/sqlite-edge-vector-rag',
        element: <SuspenseWrapper><SqliteEdgeVectorRag /></SuspenseWrapper>,
      },
      {
        path: 'sqlite-edge-vector-rag',
        element: <SuspenseWrapper><SqliteEdgeVectorRag /></SuspenseWrapper>,
      },
      {
        path: 'wt/realtime-order-stream-pipeline',
        element: <SuspenseWrapper><RealtimeOrderStreamPipeline /></SuspenseWrapper>,
      },
      {
        path: 'realtime-order-stream-pipeline',
        element: <SuspenseWrapper><RealtimeOrderStreamPipeline /></SuspenseWrapper>,
      },
      {
        path: 'wt/multi-tenant-rag-isolation',
        element: <SuspenseWrapper><MultiTenantRagIsolation /></SuspenseWrapper>,
      },
      {
        path: 'multi-tenant-rag-isolation',
        element: <SuspenseWrapper><MultiTenantRagIsolation /></SuspenseWrapper>,
      },
      {
        path: 'wt/durable-object-sqlite-state-machine',
        element: <SuspenseWrapper><DurableObjectSqliteStateMachine /></SuspenseWrapper>,
      },
      {
        path: 'durable-object-sqlite-state-machine',
        element: <SuspenseWrapper><DurableObjectSqliteStateMachine /></SuspenseWrapper>,
      },
      {
        path: 'wt/edge-semantic-cache-kv',
        element: <SuspenseWrapper><EdgeSemanticCacheKv /></SuspenseWrapper>,
      },
      {
        path: 'edge-semantic-cache-kv',
        element: <SuspenseWrapper><EdgeSemanticCacheKv /></SuspenseWrapper>,
      },
      {
        path: 'wt/vectorize-hnsw-hybrid-search',
        element: <SuspenseWrapper><VectorizeHnswHybridSearch /></SuspenseWrapper>,
      },
      {
        path: 'vectorize-hnsw-hybrid-search',
        element: <SuspenseWrapper><VectorizeHnswHybridSearch /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-durable-streams',
        element: <SuspenseWrapper><CloudflareDurableStreams /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-durable-streams',
        element: <SuspenseWrapper><CloudflareDurableStreams /></SuspenseWrapper>,
      },
      {
        path: 'wt/hyperdrive-connection-pooling',
        element: <SuspenseWrapper><HyperdriveConnectionPooling /></SuspenseWrapper>,
      },
      {
        path: 'hyperdrive-connection-pooling',
        element: <SuspenseWrapper><HyperdriveConnectionPooling /></SuspenseWrapper>,
      },
      {
        path: 'wt/zero-trust-vector-routing',
        element: <SuspenseWrapper><ZeroTrustVectorRouting /></SuspenseWrapper>,
      },
      {
        path: 'zero-trust-vector-routing',
        element: <SuspenseWrapper><ZeroTrustVectorRouting /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-workflows-engine',
        element: <SuspenseWrapper><CloudflareWorkflowsEngine /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-workflows-engine',
        element: <SuspenseWrapper><CloudflareWorkflowsEngine /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-kv-consistency-model',
        element: <SuspenseWrapper><CloudflareKvConsistencyModel /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-kv-consistency-model',
        element: <SuspenseWrapper><CloudflareKvConsistencyModel /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-d1-distributed-sqlite',
        element: <SuspenseWrapper><CloudflareD1DistributedSqlite /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-d1-distributed-sqlite',
        element: <SuspenseWrapper><CloudflareD1DistributedSqlite /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-vectorize-metadata-indexing',
        element: <SuspenseWrapper><CloudflareVectorizeMetadataIndexing /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-vectorize-metadata-indexing',
        element: <SuspenseWrapper><CloudflareVectorizeMetadataIndexing /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-durable-objects-sqlite-storage',
        element: <SuspenseWrapper><CloudflareDurableObjectsSqlite /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-durable-objects-sqlite-storage',
        element: <SuspenseWrapper><CloudflareDurableObjectsSqlite /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-hyperdrive-tcp-multiplexing',
        element: <SuspenseWrapper><CloudflareHyperdriveTcpMultiplexing /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-hyperdrive-tcp-multiplexing',
        element: <SuspenseWrapper><CloudflareHyperdriveTcpMultiplexing /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-zero-trust-access-mesh',
        element: <SuspenseWrapper><CloudflareZeroTrustAccessMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-zero-trust-access-mesh',
        element: <SuspenseWrapper><CloudflareZeroTrustAccessMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-queues-idempotent-processing',
        element: <SuspenseWrapper><CloudflareQueuesIdempotentProcessing /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-queues-idempotent-processing',
        element: <SuspenseWrapper><CloudflareQueuesIdempotentProcessing /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-browser-rendering-fleet',
        element: <SuspenseWrapper><CloudflareBrowserRenderingFleet /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-browser-rendering-fleet',
        element: <SuspenseWrapper><CloudflareBrowserRenderingFleet /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-kv-global-session-replication',
        element: <SuspenseWrapper><CloudflareKvGlobalSessionReplication /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-kv-global-session-replication',
        element: <SuspenseWrapper><CloudflareKvGlobalSessionReplication /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-vectorize-hybrid-search-pipeline',
        element: <SuspenseWrapper><CloudflareVectorizeHybridSearchPipeline /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-vectorize-hybrid-search-pipeline',
        element: <SuspenseWrapper><CloudflareVectorizeHybridSearchPipeline /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-durable-objects-web-standards-sqlite',
        element: <SuspenseWrapper><CloudflareDurableObjectsWebStandardsSqlite /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-durable-objects-web-standards-sqlite',
        element: <SuspenseWrapper><CloudflareDurableObjectsWebStandardsSqlite /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-workers-ai-function-calling-agent',
        element: <SuspenseWrapper><CloudflareWorkersAiFunctionCallingAgent /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-workers-ai-function-calling-agent',
        element: <SuspenseWrapper><CloudflareWorkersAiFunctionCallingAgent /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-hyperdrive-tcp-connection-pooling',
        element: <SuspenseWrapper><CloudflareHyperdriveTcpConnectionPooling /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-hyperdrive-tcp-connection-pooling',
        element: <SuspenseWrapper><CloudflareHyperdriveTcpConnectionPooling /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-pipelines-stream-ingest',
        element: <SuspenseWrapper><CloudflarePipelinesStreamIngest /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-pipelines-stream-ingest',
        element: <SuspenseWrapper><CloudflarePipelinesStreamIngest /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-d1-read-replication-offload',
        element: <SuspenseWrapper><CloudflareD1ReadReplicationOffload /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-d1-read-replication-offload',
        element: <SuspenseWrapper><CloudflareD1ReadReplicationOffload /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-rpc-service-bindings-mesh',
        element: <SuspenseWrapper><CloudflareRpcServiceBindingsMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-rpc-service-bindings-mesh',
        element: <SuspenseWrapper><CloudflareRpcServiceBindingsMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-calls-webrtc-media-mesh',
        element: <SuspenseWrapper><CloudflareCallsWebRtcMediaMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-calls-webrtc-media-mesh',
        element: <SuspenseWrapper><CloudflareCallsWebRtcMediaMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-workers-assets-routing',
        element: <SuspenseWrapper><CloudflareWorkersAssetsRouting /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-workers-assets-routing',
        element: <SuspenseWrapper><CloudflareWorkersAssetsRouting /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-browser-rendering-playwright-grid',
        element: <SuspenseWrapper><CloudflareBrowserRenderingPlaywrightGrid /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-browser-rendering-playwright-grid',
        element: <SuspenseWrapper><CloudflareBrowserRenderingPlaywrightGrid /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-kv-consistency-replication-mesh',
        element: <SuspenseWrapper><CloudflareKvConsistencyReplicationMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-kv-consistency-replication-mesh',
        element: <SuspenseWrapper><CloudflareKvConsistencyReplicationMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-durable-objects-sqlite-storage-mesh',
        element: <SuspenseWrapper><CloudflareDurableObjectsSqliteStorageMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-durable-objects-sqlite-storage-mesh',
        element: <SuspenseWrapper><CloudflareDurableObjectsSqliteStorageMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-vectorize-hybrid-rag-ann-index',
        element: <SuspenseWrapper><CloudflareVectorizeHybridRagAnnIndex /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-vectorize-hybrid-rag-ann-index',
        element: <SuspenseWrapper><CloudflareVectorizeHybridRagAnnIndex /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-workflows-idempotent-execution-engine',
        element: <SuspenseWrapper><CloudflareWorkflowsIdempotentExecutionEngine /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-workflows-idempotent-execution-engine',
        element: <SuspenseWrapper><CloudflareWorkflowsIdempotentExecutionEngine /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-hyperdrive-connection-pooling-mesh',
        element: <SuspenseWrapper><CloudflareHyperdriveConnectionPoolingMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-hyperdrive-connection-pooling-mesh',
        element: <SuspenseWrapper><CloudflareHyperdriveConnectionPoolingMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-kv-eventual-consistency-caching-mesh',
        element: <SuspenseWrapper><CloudflareKvEventualConsistencyCachingMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-kv-eventual-consistency-caching-mesh',
        element: <SuspenseWrapper><CloudflareKvEventualConsistencyCachingMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-queues-fifo-concurrency-throttle',
        element: <SuspenseWrapper><CloudflareQueuesFifoConcurrencyThrottle /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-queues-fifo-concurrency-throttle',
        element: <SuspenseWrapper><CloudflareQueuesFifoConcurrencyThrottle /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-browser-rendering-pdf-generation-mesh',
        element: <SuspenseWrapper><CloudflareBrowserRenderingPdfGenerationMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-browser-rendering-pdf-generation-mesh',
        element: <SuspenseWrapper><CloudflareBrowserRenderingPdfGenerationMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-ai-gateway-semantic-cache-routing',
        element: <SuspenseWrapper><CloudflareAiGatewaySemanticCacheRouting /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-ai-gateway-semantic-cache-routing',
        element: <SuspenseWrapper><CloudflareAiGatewaySemanticCacheRouting /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-workers-for-platforms-dynamic-dispatch',
        element: <SuspenseWrapper><CloudflareWorkersForPlatformsDynamicDispatch /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-workers-for-platforms-dynamic-dispatch',
        element: <SuspenseWrapper><CloudflareWorkersForPlatformsDynamicDispatch /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-vectorize-metadata-filtering-knn-partitioning',
        element: <SuspenseWrapper><CloudflareVectorizeMetadataFilteringKnnPartitioning /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-vectorize-metadata-filtering-knn-partitioning',
        element: <SuspenseWrapper><CloudflareVectorizeMetadataFilteringKnnPartitioning /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-d1-read-replication-sessions',
        element: <SuspenseWrapper><CloudflareD1ReadReplicationSessions /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-d1-read-replication-sessions',
        element: <SuspenseWrapper><CloudflareD1ReadReplicationSessions /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-turnstile-managed-challenge-bot-protection',
        element: <SuspenseWrapper><CloudflareTurnstileManagedChallengeBotProtection /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-turnstile-managed-challenge-bot-protection',
        element: <SuspenseWrapper><CloudflareTurnstileManagedChallengeBotProtection /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-kv-metadata-ttl-purge',
        element: <SuspenseWrapper><CloudflareKvMetadataTtlPurge /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-kv-metadata-ttl-purge',
        element: <SuspenseWrapper><CloudflareKvMetadataTtlPurge /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-queues-dead-letter-consumer-backoff',
        element: <SuspenseWrapper><CloudflareQueuesDeadLetterConsumerBackoff /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-queues-dead-letter-consumer-backoff',
        element: <SuspenseWrapper><CloudflareQueuesDeadLetterConsumerBackoff /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-r2-object-storage-egress-mesh',
        element: <SuspenseWrapper><CloudflareR2ObjectStorageEgressMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-r2-object-storage-egress-mesh',
        element: <SuspenseWrapper><CloudflareR2ObjectStorageEgressMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/cloudflare-images-transformations-mesh',
        element: <SuspenseWrapper><CloudflareImagesTransformationsMesh /></SuspenseWrapper>,
      },
      {
        path: 'cloudflare-images-transformations-mesh',
        element: <SuspenseWrapper><CloudflareImagesTransformationsMesh /></SuspenseWrapper>,
      },
      {
        path: 'wt/ki-automatisierung',
        element: <SuspenseWrapper><KiAutomatisierung /></SuspenseWrapper>,
      },
      {
        path: 'wt/sandbox/shopbeaver-admin',
        element: <SuspenseWrapper><ShopBeaverAdminSandbox /></SuspenseWrapper>,
      },
      {
        path: 'wt/sandbox/osint-explorer',
        element: <SuspenseWrapper><OsintOwlExplorer /></SuspenseWrapper>,
      },
      {
        path: 'wt/ops/sync-center',
        element: <SuspenseWrapper><BkbSyncCenter /></SuspenseWrapper>,
      },
      {
        path: 'wt/ops/litellm-dashboard',
        element: <SuspenseWrapper><LiteLlmDashboard /></SuspenseWrapper>,
      },
      {
        path: 'wt/ops/scanopy-monitor',
        element: <SuspenseWrapper><ScanopyMonitorDashboard /></SuspenseWrapper>,
      },
      {
        path: 'wt/ops/security-ferret',
        element: <SuspenseWrapper><SecurityFerretDashboard /></SuspenseWrapper>,
      },
      {
        path: 'wt/ops/affiliate-falcon',
        element: <SuspenseWrapper><AffiliateFalconDashboard /></SuspenseWrapper>,
      },
      {
        path: 'wt/ops/serviceant-docs',
        element: <SuspenseWrapper><ServiceAntDocsDashboard /></SuspenseWrapper>,
      },
      {
        path: 'wt/ops/command-cobra',
        element: <SuspenseWrapper><CommandCobraDashboard /></SuspenseWrapper>,
      },
      {
        path: 'wt/accounting-fox',
        element: <SuspenseWrapper><AccountingFoxShowcase /></SuspenseWrapper>,
      },
      {
        path: 'wt/inventar-owl',
        element: <SuspenseWrapper><InventarOwlShowcase /></SuspenseWrapper>,
      },
      {
        path: 'wt/market-shark',
        element: <SuspenseWrapper><MarketSharkShowcase /></SuspenseWrapper>,
      },
      {
        path: 'wt/osint-owl',
        element: <SuspenseWrapper><OsintOwlShowcase /></SuspenseWrapper>,
      },
      {
        path: 'wt/infrastructure-dog',
        element: <SuspenseWrapper><InfrastructureDogShowcase /></SuspenseWrapper>,
      },
      {
        path: 'wt/ansicht-auf-zuruf',
        element: <SuspenseWrapper><AnsichtAufZuruf /></SuspenseWrapper>,
      },
      {
        path: 'wt/performance',
        element: <SuspenseWrapper><PerformanceShowcase /></SuspenseWrapper>,
      },
      {
        path: 'apps/accounting-fox',
        element: <Navigate to="/wt/accounting-fox" replace />,
      },
      {
        path: 'apps/inventar-owl',
        element: <Navigate to="/wt/inventar-owl" replace />,
      },
      {
        path: 'apps/market-shark',
        element: <Navigate to="/wt/market-shark" replace />,
      },
      {
        path: 'apps/osint-owl',
        element: <Navigate to="/wt/osint-owl" replace />,
      },
      {
        path: 'apps/infrastructure-dog',
        element: <Navigate to="/wt/infrastructure-dog" replace />,
      },
      {
        path: 'wt/showcase/accounting-fox',
        element: <Navigate to="/wt/accounting-fox" replace />,
      },
      {
        path: 'wt/showcase/inventar-owl',
        element: <Navigate to="/wt/inventar-owl" replace />,
      },
      {
        path: 'wt/showcase/market-shark',
        element: <Navigate to="/wt/market-shark" replace />,
      },
      {
        path: 'wt/showcase/osint-owl',
        element: <Navigate to="/wt/osint-owl" replace />,
      },
      {
        path: 'wt/showcase/infrastructure-dog',
        element: <Navigate to="/wt/infrastructure-dog" replace />,
      },

      {
        path: 'bkb',
        element: <SuspenseWrapper><BauKlotzBude /></SuspenseWrapper>,
      },
      {
        path: 'impressum',
        element: <SuspenseWrapper><Impressum /></SuspenseWrapper>,
      },
      {
        path: 'about',
        element: <SuspenseWrapper><About /></SuspenseWrapper>,
      },
      {
        path: 'services',
        element: <SuspenseWrapper><Services /></SuspenseWrapper>,
      },
      {
        path: 'shipping-returns',
        element: <SuspenseWrapper><ShippingReturns /></SuspenseWrapper>,
      },
      {
        path: 'faq',
        element: <SuspenseWrapper><Faq /></SuspenseWrapper>,
      },
      {
        path: 'blog/cloudflare-workers-tipps',
        element: <SuspenseWrapper><CloudflareWorkersTipps /></SuspenseWrapper>,
      },
      {
        path: 'blog/hono-d1-database',
        element: <SuspenseWrapper><HonoD1Database /></SuspenseWrapper>,
      },
      {
        path: 'services/devops',
        element: <SuspenseWrapper><DevOpsMonitoring /></SuspenseWrapper>,
      },
      {
        path: 'services/ai',
        element: <SuspenseWrapper><ServicesAi /></SuspenseWrapper>,
      },
      {
        path: 'datenschutz',
        element: <SuspenseWrapper><Datenschutz /></SuspenseWrapper>,
      },

    ]),
  },
], {
  basename: import.meta.env.BASE_URL.replace(/\/$/, '') || '/',
});
